<template>
    <div class="widget widget-corrections-queue h-100 dashboard-fila-correcoes-revisoes">
        <div class="widget-heading dashboard-fila-correcoes-revisoes__heading">
            <template v-if="mostrarAbasRevisao">
                <ul class="nav nav-pills dashboard-fila-correcoes-revisoes__tabs flex-grow-1" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button
                            type="button"
                            class="nav-link"
                            :class="{ active: abaAtiva === 'correcoes' }"
                            role="tab"
                            :aria-selected="abaAtiva === 'correcoes'"
                            @click="abaAtiva = 'correcoes'"
                        >
                            Correções
                            <span
                                class="dashboard-fila-correcoes-revisoes__tab-count"
                                :aria-label="`${contagemCorrecoes} na fila de correções`"
                            >
                                {{ contagemCorrecoes }}
                            </span>
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button
                            type="button"
                            class="nav-link"
                            :class="{ active: abaAtiva === 'revisoes' }"
                            role="tab"
                            :aria-selected="abaAtiva === 'revisoes'"
                            @click="abaAtiva = 'revisoes'"
                        >
                            Revisões de texto
                            <span
                                class="dashboard-fila-correcoes-revisoes__tab-count"
                                :aria-label="`${contagemRevisoes} na fila de revisões de texto`"
                            >
                                {{ contagemRevisoes }}
                            </span>
                        </button>
                    </li>
                </ul>
            </template>
            <template v-else>
                <div class="dashboard-fila-correcoes-revisoes__title-group">
                    <h5>Fila de Correções</h5>
                    <span class="badge badge-primary">{{ itensCorrecoes.length }}</span>
                </div>
            </template>
            <button
                type="button"
                class="dashboard-fila-correcoes-revisoes__enviar-btn"
                title="Enviar uma nova redação"
                @click="onEnviarRedacaoClick"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M12 5v14M5 12h14" />
                </svg>
                <span class="dashboard-fila-correcoes-revisoes__enviar-btn-label">Enviar redação</span>
            </button>
        </div>
        <div class="widget-content dashboard-fila-correcoes-revisoes__body">
            <FilaPendentesCard
                v-show="!mostrarAbasRevisao || abaAtiva === 'correcoes'"
                embedded
                :items="itensCorrecoes"
                :loading="loadingCorrecoes"
                empty-message="Nenhuma correção pendente"
                empty-state-title="Nenhuma correção na fila"
                empty-state-description="Suas correções pendentes aparecerão aqui."
                empty-state-button-text="Enviar uma redação"
                :on-empty-state-click="onEmptyCorrecoesClick"
                :on-item-click="onCorrecaoClick"
            />
            <FilaPendentesCard
                v-show="mostrarAbasRevisao && abaAtiva === 'revisoes'"
                embedded
                :items="filaRevisoesFormatada"
                :loading="loadingFilaRevisoes"
                action-button-title="Revisar texto"
                empty-message="Nenhuma redação aguardando revisão de texto no momento."
                empty-state-sem-botao
                empty-state-title="Nenhuma revisão na fila"
                empty-state-description="Suas revisões de texto pendentes aparecerão aqui quando houver redações nas turmas em que você é revisor designado."
                :on-item-click="onRevisaoClick"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useUserStore } from '@/stores/user';
import FilaPendentesCard from '@/components/widgets/FilaPendentesCard.vue';
import {
    fetchHumanTextReviewFila,
    normalizeDocumentoFilaRevisaoHumana,
} from '@/api/humanTextReview';

const props = defineProps({
    itensCorrecoes: {
        type: Array,
        default: () => [],
    },
    loadingCorrecoes: {
        type: Boolean,
        default: false,
    },
    onCorrecaoClick: {
        type: Function,
        default: null,
    },
    onEmptyCorrecoesClick: {
        type: Function,
        default: null,
    },
});

const router = useRouter();
const store = useStore();
const userStore = useUserStore();

const abaAtiva = ref('correcoes');
const filaRevisoesRaw = ref([]);
const loadingFilaRevisoes = ref(false);

function valorAceitaRevisoesTerceiros(u) {
    if (!u) return false;
    const v = u.aceita_revisoes_texto_terceiros;
    return v === true || v === 1 || v === '1';
}

const aceitaRevisoesTerceiros = computed(() => {
    const pinia = userStore.user;
    const auth = store.state.auth?.user;
    if (valorAceitaRevisoesTerceiros(pinia)) return true;
    if (valorAceitaRevisoesTerceiros(auth)) return true;
    return false;
});

const mostrarAbasRevisao = computed(() => aceitaRevisoesTerceiros.value);

const contagemCorrecoes = computed(() => props.itensCorrecoes?.length ?? 0);

const contagemRevisoes = computed(() => filaRevisoesFormatada.value.length);

function calcularTempoEspera(dataSubmissao) {
    if (!dataSubmissao) return 'Data não disponível';
    const agora = new Date();
    const submissao = new Date(dataSubmissao);
    const diffInSeconds = Math.floor((agora - submissao) / 1000);
    if (diffInSeconds < 60) return 'agora';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    }
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
        return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    }
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `há ${diffInMonths} ${diffInMonths === 1 ? 'mês' : 'meses'}`;
    }
    const diffInYears = Math.floor(diffInMonths / 12);
    return `há ${diffInYears} ${diffInYears === 1 ? 'ano' : 'anos'}`;
}

const filaRevisoesFormatada = computed(() => {
    return filaRevisoesRaw.value
        .map((doc) => {
            const base = normalizeDocumentoFilaRevisaoHumana(doc);
            if (!base) return null;
            return {
                ...base,
                tempoEspera: calcularTempoEspera(base.data_submissao),
            };
        })
        .filter(Boolean);
});

async function carregarFilaRevisoes() {
    if (!aceitaRevisoesTerceiros.value) {
        filaRevisoesRaw.value = [];
        return;
    }
    loadingFilaRevisoes.value = true;
    try {
        const { items } = await fetchHumanTextReviewFila({ per_page: 15 });
        filaRevisoesRaw.value = Array.isArray(items) ? items : [];
    } catch (e) {
        console.error('Erro ao carregar fila de revisões de texto:', e);
        filaRevisoesRaw.value = [];
    } finally {
        loadingFilaRevisoes.value = false;
    }
}

function onRevisaoClick(item) {
    if (!item?.id) return;
    router.push({
        name: 'documento-preview',
        params: { id: item.id },
        query: { revisao_texto_humano: '1' },
    });
}

function onEnviarRedacaoClick() {
    if (typeof props.onEmptyCorrecoesClick === 'function') {
        props.onEmptyCorrecoesClick();
        return;
    }
    router.push({ name: 'documento' });
}

onMounted(async () => {
    try {
        await userStore.getUser();
    } catch {
        /* auth pode já ter o usuário */
    }
    await carregarFilaRevisoes();
});

watch(aceitaRevisoesTerceiros, (ativo) => {
    if (ativo) {
        carregarFilaRevisoes();
    } else {
        filaRevisoesRaw.value = [];
        abaAtiva.value = 'correcoes';
    }
});
</script>

<style scoped>
.dashboard-fila-correcoes-revisoes__heading {
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
    align-items: center;
    width: 100%;
}

.dashboard-fila-correcoes-revisoes__title-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
}

.dashboard-fila-correcoes-revisoes__title-group h5 {
    margin: 0;
}

.dashboard-fila-correcoes-revisoes__enviar-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    flex-shrink: 0;
    margin-left: auto;
    padding: 0.35rem 0.65rem;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
    color: #4361ee;
    background: rgba(67, 97, 238, 0.08);
    border: 1px solid rgba(67, 97, 238, 0.35);
    border-radius: 8px;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.dashboard-fila-correcoes-revisoes__enviar-btn:hover {
    color: #fff;
    background: #4361ee;
    border-color: #4361ee;
}

.dashboard-fila-correcoes-revisoes__enviar-btn:focus-visible {
    outline: 2px solid #4361ee;
    outline-offset: 2px;
}

@media (max-width: 420px) {
    .dashboard-fila-correcoes-revisoes__enviar-btn-label {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .dashboard-fila-correcoes-revisoes__enviar-btn {
        padding: 0.4rem;
    }
}

.dashboard-fila-correcoes-revisoes__tabs {
    flex-wrap: wrap;
    margin-bottom: 0;
    padding-left: 0;
    list-style: none;
    gap: 0.35rem;
}

.dashboard-fila-correcoes-revisoes__tabs .nav-link {
    font-size: 0.8125rem;
    font-weight: 600;
    padding: 0.35rem 0.75rem;
    border-radius: 8px;
    color: #6b7280;
    border: 1px solid transparent;
    background: transparent;
    line-height: 1.2;
}

.dashboard-fila-correcoes-revisoes__tabs .nav-link:hover {
    color: #4361ee;
    background: rgba(67, 97, 238, 0.08);
}

.dashboard-fila-correcoes-revisoes__tabs .nav-link.active {
    color: #fff;
    background: #4361ee;
    border-color: #4361ee;
}

.dashboard-fila-correcoes-revisoes__tab-count {
    display: inline-block;
    min-width: 1.25rem;
    margin-left: 0.35rem;
    padding: 0.1rem 0.45rem;
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    border-radius: 999px;
    background: rgba(67, 97, 238, 0.15);
    color: #4361ee;
    vertical-align: middle;
}

.dashboard-fila-correcoes-revisoes__tabs .nav-link.active .dashboard-fila-correcoes-revisoes__tab-count {
    background: rgba(255, 255, 255, 0.28);
    color: #fff;
}

.dashboard-fila-correcoes-revisoes__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__tabs .nav-link,
.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link,
body.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link {
    color: #888ea8;
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__tabs .nav-link:hover,
.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link:hover,
body.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link:hover {
    color: #e0e6ed;
    background: rgba(67, 97, 238, 0.2);
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__tabs .nav-link.active,
.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link.active,
body.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link.active {
    color: #fff;
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__tab-count,
.dark .dashboard-fila-correcoes-revisoes__tab-count,
body.dark .dashboard-fila-correcoes-revisoes__tab-count {
    background: rgba(67, 97, 238, 0.35);
    color: #b4c2ff;
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__tabs .nav-link.active .dashboard-fila-correcoes-revisoes__tab-count,
.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link.active .dashboard-fila-correcoes-revisoes__tab-count,
body.dark .dashboard-fila-correcoes-revisoes__tabs .nav-link.active .dashboard-fila-correcoes-revisoes__tab-count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__enviar-btn,
.dark .dashboard-fila-correcoes-revisoes__enviar-btn,
body.dark .dashboard-fila-correcoes-revisoes__enviar-btn {
    color: #b4c2ff;
    background: rgba(67, 97, 238, 0.2);
    border-color: rgba(67, 97, 238, 0.45);
}

[data-bs-theme='dark'] .dashboard-fila-correcoes-revisoes__enviar-btn:hover,
.dark .dashboard-fila-correcoes-revisoes__enviar-btn:hover,
body.dark .dashboard-fila-correcoes-revisoes__enviar-btn:hover {
    color: #fff;
    background: #4361ee;
    border-color: #4361ee;
}
</style>